import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { BackboneAdapterService } from '../../shared/backbone-adapter.service';
import { GTMService } from '../../shared/gtm.service';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { ProfileServiceConfigurationService } from '../../shared/profile-service-configuration.service';
import { ProfileService } from '../../shared/profile.service';
import { GTMEvent } from '../../shared/types/GTMEvent';
import { LiveClassDto } from '../../shared/types/LiveClassDto';
import { LiveClassEventState } from '../../shared/types/LiveClassEventState';
import { LiveClassEventType } from '../../shared/types/LiveClassEventType';
import { BilingualFallbackType } from '../../shared/types/bilingual-fallback-type';
import { GamificationActivityType } from '../../shared/types/gamification-activity-type';
import { LCOnboardingTrackEvent } from '../../shared/types/lc-onboarding-track-event';
import { NotificationDto } from '../../shared/types/notification-dto';
import { TeacherType } from '../../shared/types/teacher-type';
import { LiveClassEventDto } from '../live-class-event-dto';
import { LiveClassEventService } from '../live-class-event.service';
import { LiveClassService } from '../live-class.service';
import {
  getClassBeginFromFirstsWithNoLiveClassStatus,
  getUniqueOrderedLiveClassesBySubtype,
} from './services/live-class-array-utils';
import { LiveClassCountdownTimerService } from './services/live-class-countdown-timer.service';

@Component({
  selector: 'lp2-live-class-section',
  templateUrl: './live-class-section.component.html',
  styleUrls: ['./live-class-section.component.scss'],
})
export class LiveClassSectionComponent implements OnInit, OnDestroy {
  @Input() activityPoints: Map<GamificationActivityType, number>;
  @Input() preferredTeacherType: TeacherType;
  @Input() fallBackLiveClassTeacherType: BilingualFallbackType;

  public liveClasses: Array<LiveClassDto>;
  private subscriptions = new Subscription();
  public teacherControl: FormControl = new FormControl();

  public get isSwitchVisible$(): Observable<boolean> {
    return combineLatest([
      this.profileServiceConfigurationService.isLiveClassOnboardingEnabled$(),
      of(this.oeAppPersonService.isBilingualEnabled),
    ]).pipe(
      map(([isLiveClassOnboardingEnabled, isBilingualEnabled]) => isLiveClassOnboardingEnabled && isBilingualEnabled)
    );
  }

  constructor(
    private backbone: BackboneAdapterService,
    private liveClassEventService: LiveClassEventService,
    private liveClassService: LiveClassService,
    private gtmService: GTMService,
    private liveClassCountdownTimerService: LiveClassCountdownTimerService,
    private profileServiceConfigurationService: ProfileServiceConfigurationService,
    private oeAppPersonService: OeAppPersonService,
    private profileService: ProfileService
  ) {}

  async ngOnInit() {
    this.teacherControl.valueChanges.subscribe(({teacherType, bilingualFallback}: {teacherType: TeacherType, bilingualFallback: BilingualFallbackType}) => this.updatePreferredTeacherType(teacherType, bilingualFallback));
    this.teacherControl.setValue({teacherType: this.preferredTeacherType, bilingualFallback: this.fallBackLiveClassTeacherType }, { emitEvent: false });

    this.backbone.addListener('liveclass:class-full', (notification: NotificationDto) => {
      this.fetchCurrentLiveClassSchedules();
      if (notification) {
        this.gtmService.sendEvent(GTMEvent.LIVE_CLASS_ACTION, {
          state: LiveClassEventState.CLASS_REJECTED,
          liveclassId: notification.scheduleId,
          type: LiveClassEventType.SYSTEM,
        });
      }
    });

    this.subscriptions.add(
      this.liveClassEventService.onChange.subscribe((event: LiveClassEventDto) => {
        if (!event) {
          return;
        }
        if (event.event === 'NEEDS_NEW_CLASSES') {
          this.liveClassCountdownTimerService.setClassBegin(null);
          this.fetchNextLiveClassSchedules();
        }
      })
    );

    this.fetchCurrentLiveClassSchedules();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.liveClassCountdownTimerService.destroy();
  }

  private updatePreferredTeacherType(preferredTeacherType: TeacherType, fallBackLiveClassTeacherType?: BilingualFallbackType) {
    this.profileService
      .updateProfile(this.oeAppPersonService.id, { preferredTeacherType, fallBackLiveClassTeacherType })
      .subscribe();
    this.gtmService.sendGA4Event(
      preferredTeacherType === TeacherType.BILINGUAL
        ? LCOnboardingTrackEvent.LC_CHANGE_TYPE_OF_TEACHER_CONFIRM_BILINGUAL
        : LCOnboardingTrackEvent.LC_CHANGE_TYPE_OF_TEACHER_CONFIRM_NATIVE
    );
  }

  fetchCurrentLiveClassSchedules(): void {
    let newLiveClasses = [];
    this.subscriptions.add(
      this.liveClassService
        .getInProgressLiveClassSchedules()
        .pipe(
          mergeMap((liveClasses) => {
            newLiveClasses = liveClasses;
            return this.liveClassService.getCurrentLiveClassSchedules();
          })
        )
        .subscribe((liveClasses) => {
          newLiveClasses = newLiveClasses.concat(liveClasses);
          this.onLiveClassesFetch(newLiveClasses);
        })
    );
  }

  fetchNextLiveClassSchedules(): void {
    let newLiveClasses = [];
    this.subscriptions.add(
      this.liveClassService
        .getInProgressLiveClassSchedules()
        .pipe(
          mergeMap((inProgressLiveClasses) => {
            newLiveClasses = inProgressLiveClasses;
            return this.liveClassService.getCurrentLiveClassSchedules();
          }),
          mergeMap((currentLiveClasses) => {
            const signedUpLiveClasses = currentLiveClasses.filter(
              (liveClass) => liveClass.liveClassStatus === 'SIGNED_UP'
            );
            newLiveClasses = newLiveClasses.concat(signedUpLiveClasses);
            return this.liveClassService.getNextLiveClassSchedules();
          })
        )
        .subscribe((nextLiveClasses) => {
          newLiveClasses = newLiveClasses.concat(nextLiveClasses);
          this.onLiveClassesFetch(newLiveClasses);
        })
    );
  }

  onLiveClassesFetch(liveClasses: Array<LiveClassDto>): void {
    if (!liveClasses || liveClasses.length === 0) {
      this.liveClasses = [];
      this.liveClassEventService.triggerEvent('NO_CLASSES', null);
      return;
    }

    const classBegin = getClassBeginFromFirstsWithNoLiveClassStatus(liveClasses);
    this.liveClassCountdownTimerService.setClassBegin(classBegin);
    this.liveClasses = getUniqueOrderedLiveClassesBySubtype(liveClasses);
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-section/live-class-section.component.ts